import React, { createContext, useContext, useCallback } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { generateId } from '../utils/id';
import { getTodayKey } from '../utils/date';
import { buildSeedHabits, buildSeedTasks, buildSeedBorrowedItems } from '../data/seedData';
import type { Habit } from '../types/habit';
import type { Task } from '../types/task';
import type { BorrowedItem } from '../types/borrowedItem';
interface AppDataContextValue {
  habits: Habit[];
  tasks: Task[];
  borrowedItems: BorrowedItem[];
  addHabit: (name: string) => void;
  toggleHabitToday: (id: string) => void;
  deleteHabit: (id: string) => void;
  addTask: (input: {
    title: string;
    deadline: string;
    reminder: boolean;
  }) => void;
  toggleTaskCompleted: (id: string) => void;
  deleteTask: (id: string) => void;
  addBorrowedItem: (input: {
    itemName: string;
    borrowedTo: string;
    dateBorrowed: string;
    returnDeadline?: string;
  }) => void;
  markBorrowedReturned: (id: string) => void;
  deleteBorrowedItem: (id: string) => void;
}
const AppDataContext = createContext<AppDataContextValue | undefined>(undefined);
export function AppDataProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [habits, setHabits] = useLocalStorage<Habit[]>('daily-hub-habits', buildSeedHabits);
  const [tasks, setTasks] = useLocalStorage<Task[]>('daily-hub-tasks', buildSeedTasks);
  const [borrowedItems, setBorrowedItems] = useLocalStorage<BorrowedItem[]>('daily-hub-borrowed', buildSeedBorrowedItems);
  const addHabit = useCallback((name: string) => {
    setHabits(prev => [...prev, {
      id: generateId(),
      name,
      createdAt: new Date().toISOString(),
      completions: []
    }]);
  }, [setHabits]);
  const toggleHabitToday = useCallback((id: string) => {
    const todayKey = getTodayKey();
    setHabits(prev => prev.map(habit => habit.id === id ? {
      ...habit,
      completions: habit.completions.includes(todayKey) ? habit.completions.filter(d => d !== todayKey) : [...habit.completions, todayKey]
    } : habit));
  }, [setHabits]);
  const deleteHabit = useCallback((id: string) => {
    setHabits(prev => prev.filter(habit => habit.id !== id));
  }, [setHabits]);
  const addTask = useCallback((input: {
    title: string;
    deadline: string;
    reminder: boolean;
  }) => {
    setTasks(prev => [...prev, {
      id: generateId(),
      ...input,
      completed: false,
      createdAt: new Date().toISOString()
    }]);
  }, [setTasks]);
  const toggleTaskCompleted = useCallback((id: string) => {
    setTasks(prev => prev.map(task => task.id === id ? {
      ...task,
      completed: !task.completed
    } : task));
  }, [setTasks]);
  const deleteTask = useCallback((id: string) => {
    setTasks(prev => prev.filter(task => task.id !== id));
  }, [setTasks]);
  const addBorrowedItem = useCallback((input: {
    itemName: string;
    borrowedTo: string;
    dateBorrowed: string;
    returnDeadline?: string;
  }) => {
    setBorrowedItems(prev => [...prev, {
      id: generateId(),
      ...input,
      returned: false
    }]);
  }, [setBorrowedItems]);
  const markBorrowedReturned = useCallback((id: string) => {
    setBorrowedItems(prev => prev.map(item => item.id === id ? {
      ...item,
      returned: true,
      returnedAt: new Date().toISOString()
    } : item));
  }, [setBorrowedItems]);
  const deleteBorrowedItem = useCallback((id: string) => {
    setBorrowedItems(prev => prev.filter(item => item.id !== id));
  }, [setBorrowedItems]);
  const value: AppDataContextValue = {
    habits,
    tasks,
    borrowedItems,
    addHabit,
    toggleHabitToday,
    deleteHabit,
    addTask,
    toggleTaskCompleted,
    deleteTask,
    addBorrowedItem,
    markBorrowedReturned,
    deleteBorrowedItem
  };
  return <AppDataContext.Provider value={value} data-id="element-155" data-mp="3mf7pbtmz0rsi">{children}</AppDataContext.Provider>;
}
export function useAppData(): AppDataContextValue {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error('useAppData must be used within AppDataProvider');
  return ctx;
}